import { useEffect, useId, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { PollMarkdownContent } from "../../../components/polls/poll-markdown-content";
import { isDeadlineInPast, validateMaxSelections } from "../../../lib/basic-polls/validate-poll";
import { resolveBasicPollDeadline } from "../../../lib/voting-deadlines";
import { createReminderSettingsSnapshot } from "../../reminders/reminder-settings";
import {
  PollNotificationSettings,
} from "../../reminders/PollNotificationSettings";
import { pollReminderSummary } from "../../reminders/poll-notification-settings";

function buildDefaultOptions() {
  return [
    { id: `option-${crypto.randomUUID()}`, label: "", note: "" },
    { id: `option-${crypto.randomUUID()}`, label: "", note: "" },
  ];
}

function resolveDeadlineDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDeadlineLocalValue(dateValue) {
  const date = resolveDeadlineDate(dateValue);
  if (!date) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

function createInitialDraft(initialPoll, inheritedReminderSettings) {
  if (!initialPoll) {
    return {
      title: "",
      description: "",
      voteType: "MULTIPLE_CHOICE",
      allowMultiple: false,
      maxSelections: "",
      allowWriteIn: false,
      required: false,
      deadlineAtLocal: "",
      reminders: createReminderSettingsSnapshot(inheritedReminderSettings),
      options: buildDefaultOptions(),
      descriptionTab: "write",
      noteEditor: { optionId: null, tab: "write", value: "" },
    };
  }

  const settings = initialPoll.settings || {};
  const voteType = settings.voteType || "MULTIPLE_CHOICE";
  const options = Array.isArray(initialPoll.options) ? initialPoll.options : [];
  const normalizedOptions = options.length
    ? options
        .slice()
        .sort((left, right) => {
          const leftOrder = Number.isFinite(left?.order) ? left.order : Number.MAX_SAFE_INTEGER;
          const rightOrder = Number.isFinite(right?.order) ? right.order : Number.MAX_SAFE_INTEGER;
          return leftOrder - rightOrder;
        })
        .map((option, index) => ({
          id: option?.id || `option-${index + 1}`,
          label: option?.label || "",
          note: option?.note || "",
        }))
    : buildDefaultOptions();
  const deadlineAtLocal = toDeadlineLocalValue(resolveBasicPollDeadline(initialPoll));

  return {
    title: initialPoll.title || "",
    description: initialPoll.description || "",
    voteType,
    allowMultiple: voteType === "MULTIPLE_CHOICE" && settings.allowMultiple === true,
    maxSelections:
      voteType === "MULTIPLE_CHOICE" && Number.isFinite(settings.maxSelections)
        ? String(settings.maxSelections)
        : "",
    allowWriteIn: voteType === "MULTIPLE_CHOICE" && settings.allowWriteIn === true,
    required: initialPoll.required === true,
    deadlineAtLocal,
    reminders: createReminderSettingsSnapshot(
      initialPoll.reminders ?? inheritedReminderSettings
    ),
    options: normalizedOptions,
    descriptionTab: "write",
    noteEditor: { optionId: null, tab: "write", value: "" },
  };
}

export function EmbeddedPollEditorModal({
  open,
  onOpenChange,
  initialPoll = null,
  onSave,
  saving = false,
  isPersistedEdit = false,
  votedCount = 0,
  votesKnown = false,
  group = null,
  inheritedReminderSettings,
}) {
  const inheritedReminders = useMemo(
    () => createReminderSettingsSnapshot(inheritedReminderSettings, {
      discordLinked: Boolean(group?.discord?.channelId),
    }),
    [group?.discord?.channelId, inheritedReminderSettings]
  );
  const [draft, setDraft] = useState(() =>
    createInitialDraft(initialPoll, inheritedReminders)
  );
  const [error, setError] = useState(null);
  const [errorField, setErrorField] = useState(null);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  // Namespaced ids so the modal is safe if ever mounted twice (more robust than
  // the standalone's fixed ids). Label <-> input association is wired via these.
  const reactId = useId();
  const titleId = `${reactId}-title`;
  const descriptionId = `${reactId}-description`;
  const voteTypeId = `${reactId}-vote-type`;
  const deadlineId = `${reactId}-deadline`;
  const maxSelectionsId = `${reactId}-max-selections`;
  const noteTextareaId = `${reactId}-option-note`;
  const errorAlertId = `${reactId}-error`;

  // Structural fields (option ids/order/labels + voteType/allowMultiple/
  // maxSelections/allowWriteIn) are locked once votes exist — changing them
  // would silently rewrite the meaning of already-cast votes. Title,
  // description, option NOTES, deadline, and "required" stay editable. Vote
  // privacy is no longer set here — embedded polls inherit the parent
  // scheduler's privacy (clamped so they're never less private than stored).
  // `isPersistedEdit` is true ONLY for editing a saved scheduler poll (the modal
  // also receives `initialPoll` for draft/import in /create, which must NOT
  // lock). Fail CLOSED: lock when votes exist OR when the count is not yet
  // known, so a fast save during the initial vote-load window can't rewrite
  // structure before we know whether votes exist.
  const structuralLocked =
    isPersistedEdit && (!votesKnown || votedCount > 0);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
    setDraft(createInitialDraft(initialPoll, inheritedReminders));
    setError(null);
    setErrorField(null);
    setAdvancedExpanded(false);
  }, [inheritedReminders, open, initialPoll]);

  const selectedOption = useMemo(
    () => draft.options.find((option) => option.id === draft.noteEditor.optionId) || null,
    [draft.noteEditor.optionId, draft.options]
  );

  // Non-blocking parity warning with the standalone editor: two filled options
  // sharing a label (case/whitespace-insensitive) make votes hard to interpret.
  // Rendered as a polite status, never blocks save.
  const hasDuplicateOptionLabel = useMemo(() => {
    const filled = draft.options
      .map((option) => String(option?.label || "").trim().toLowerCase())
      .filter((label) => label.length > 0);
    return filled.length > 0 && new Set(filled).size < filled.length;
  }, [draft.options]);

  function updateOption(optionId, updates) {
    setDraft((previous) => ({
      ...previous,
      options: previous.options.map((option) =>
        option.id === optionId ? { ...option, ...updates } : option
      ),
    }));
  }

  function moveOption(optionId, direction) {
    setDraft((previous) => {
      const currentIndex = previous.options.findIndex((option) => option.id === optionId);
      if (currentIndex < 0) return previous;
      const nextIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= previous.options.length) return previous;
      const nextOptions = [...previous.options];
      const [moved] = nextOptions.splice(currentIndex, 1);
      nextOptions.splice(nextIndex, 0, moved);
      return { ...previous, options: nextOptions };
    });
  }

  function addOption() {
    setDraft((previous) => ({
      ...previous,
      options: [...previous.options, { id: `option-${crypto.randomUUID()}`, label: "", note: "" }],
    }));
  }

  function removeOption(optionId) {
    setDraft((previous) => {
      if (previous.options.length <= 2) return previous;
      return {
        ...previous,
        options: previous.options.filter((option) => option.id !== optionId),
      };
    });
  }

  function failValidation(message, field) {
    setError(message);
    setErrorField(field || null);
  }

  async function handleSave() {
    const normalizedTitle = String(draft.title || "").trim();
    if (!normalizedTitle) {
      failValidation("Poll title is required.", "title");
      return;
    }

    const normalizedOptions = draft.options
      .map((option, index) => ({
        id: option.id || `option-${index + 1}`,
        label: String(option.label || "").trim(),
        order: index,
        note: String(option.note || "").trim(),
      }))
      .filter((option) => option.label.length > 0);
    if (normalizedOptions.length < 2) {
      failValidation("At least two options are required.", "options");
      return;
    }

    const parsedMaxSelections =
      draft.voteType === "MULTIPLE_CHOICE" && draft.allowMultiple && draft.maxSelections
        ? Number(draft.maxSelections)
        : null;
    const maxSelectionsResult = validateMaxSelections({
      voteType: draft.voteType,
      allowMultiple: draft.allowMultiple,
      allowWriteIn: draft.allowWriteIn,
      optionCount: normalizedOptions.length,
      maxSelections: parsedMaxSelections,
    });
    if (!maxSelectionsResult.valid) {
      failValidation(maxSelectionsResult.message, "maxSelections");
      return;
    }

    const deadlineAt = draft.deadlineAtLocal ? new Date(draft.deadlineAtLocal) : null;
    if (deadlineAt && Number.isNaN(deadlineAt.getTime())) {
      failValidation("Deadline is invalid.", "deadline");
      return;
    }
    // CREATE: reject any past deadline. EDIT (persisted): reject a past deadline
    // ONLY if it differs from the poll's initial deadline, so renaming an
    // already-expired poll without touching its deadline stays allowed. We
    // compare the editable datetime-local representation (not raw timestamps) to
    // avoid second-precision mismatches when a stored deadline round-trips
    // through the minute-granular datetime-local control.
    if (deadlineAt && isDeadlineInPast(deadlineAt)) {
      const initialDeadlineLocal = isPersistedEdit
        ? toDeadlineLocalValue(resolveBasicPollDeadline(initialPoll || {}))
        : "";
      const isUnchangedDeadline =
        isPersistedEdit &&
        Boolean(initialDeadlineLocal) &&
        draft.deadlineAtLocal === initialDeadlineLocal;
      if (!isUnchangedDeadline) {
        failValidation("Deadline must be in the future.", "deadline");
        return;
      }
    }

    setError(null);
    setErrorField(null);
    try {
      // Vote privacy is intentionally omitted — embedded polls inherit the
      // parent scheduler's privacy at render time (resolveEmbeddedPollPrivacy).
      // Omitting it here means an edit MERGE-preserves any stored privacy on an
      // existing poll (the anonymity floor), and a new poll stores the defaults
      // that resolve to exactly the parent's settings.
      await onSave({
        title: normalizedTitle,
        description: String(draft.description || "").trim(),
        options: normalizedOptions,
        required: draft.required,
        settings: {
          voteType: draft.voteType,
          allowMultiple: draft.voteType === "MULTIPLE_CHOICE" && draft.allowMultiple,
          maxSelections:
            draft.voteType === "MULTIPLE_CHOICE" && draft.allowMultiple && parsedMaxSelections
              ? parsedMaxSelections
              : null,
          allowWriteIn: draft.voteType === "MULTIPLE_CHOICE" && draft.allowWriteIn,
          deadlineAt: deadlineAt || null,
        },
        reminders: draft.reminders,
      });
      onOpenChange(false);
    } catch (nextError) {
      setError(nextError?.message || "Failed to save embedded poll.");
      setErrorField(null);
    }
  }

  const fieldDescribedBy = (field) =>
    error && errorField === field ? errorAlertId : undefined;
  const fieldIsInvalid = (field) => Boolean(error && errorField === field);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{initialPoll ? "Edit embedded poll" : "Add embedded poll"}</DialogTitle>
            <DialogDescription>
              Configure an embedded poll for this session.
            </DialogDescription>
          </DialogHeader>

          {structuralLocked ? (
            <div
              role="status"
              className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200"
            >
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Votes have already been cast — options and vote type are locked to keep existing
                votes valid. You can still edit the title, description, option notes, deadline, and
                privacy.
              </span>
            </div>
          ) : null}

          {error ? (
            <p
              id={errorAlertId}
              role="alert"
              className="mt-3 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700 outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor={titleId} className="block text-sm font-medium text-card-foreground">
                Title
              </label>
              <input
                id={titleId}
                value={draft.title}
                onChange={(event) =>
                  setDraft((previous) => ({ ...previous, title: event.target.value }))
                }
                aria-invalid={fieldIsInvalid("title")}
                aria-describedby={fieldDescribedBy("title")}
                className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor={descriptionId}
                  className="block text-sm font-medium text-card-foreground"
                >
                  Description
                </label>
                <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((previous) => ({ ...previous, descriptionTab: "write" }))
                    }
                    aria-pressed={draft.descriptionTab === "write"}
                    className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      draft.descriptionTab === "write"
                        ? "bg-brand-primary text-white"
                        : "text-muted-foreground hover:text-card-foreground"
                    }`}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((previous) => ({ ...previous, descriptionTab: "preview" }))
                    }
                    aria-pressed={draft.descriptionTab === "preview"}
                    className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      draft.descriptionTab === "preview"
                        ? "bg-brand-primary text-white"
                        : "text-muted-foreground hover:text-card-foreground"
                    }`}
                  >
                    Preview
                  </button>
                </div>
              </div>
              {draft.descriptionTab === "write" ? (
                <textarea
                  id={descriptionId}
                  value={draft.description}
                  onChange={(event) =>
                    setDraft((previous) => ({ ...previous, description: event.target.value }))
                  }
                  rows={4}
                  className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              ) : (
                <PollMarkdownContent
                  content={draft.description}
                  fallback="_No description_"
                  className="rounded-xl border border-border bg-card px-3 py-2"
                />
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label
                  htmlFor={voteTypeId}
                  className="block text-sm font-medium text-card-foreground"
                >
                  Vote type
                </label>
                <select
                  id={voteTypeId}
                  value={draft.voteType}
                  onChange={(event) => {
                    const nextVoteType = event.target.value;
                    setDraft((previous) => ({
                      ...previous,
                      // Preserve allowMultiple/allowWriteIn/maxSelections when
                      // switching away from MULTIPLE_CHOICE so switching back
                      // restores the prior config (these only apply while
                      // voteType === MULTIPLE_CHOICE, gated in render + payload).
                      voteType: nextVoteType,
                    }));
                  }}
                  disabled={structuralLocked}
                  aria-disabled={structuralLocked}
                  className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="MULTIPLE_CHOICE">Multiple choice</option>
                  <option value="RANKED_CHOICE">Ranked choice</option>
                </select>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor={deadlineId}
                  className="block text-sm font-medium text-card-foreground"
                >
                  Deadline
                </label>
                <input
                  id={deadlineId}
                  type="datetime-local"
                  value={draft.deadlineAtLocal}
                  onChange={(event) =>
                    setDraft((previous) => ({ ...previous, deadlineAtLocal: event.target.value }))
                  }
                  aria-invalid={fieldIsInvalid("deadline")}
                  aria-describedby={fieldDescribedBy("deadline")}
                  className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>

            {draft.voteType === "MULTIPLE_CHOICE" ? (
              <div className="space-y-3 rounded-xl border border-border p-4">
                <label className="flex items-center gap-2 text-sm text-card-foreground">
                  <input
                    type="checkbox"
                    className="accent-brand-primary"
                    checked={draft.allowMultiple}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        allowMultiple: event.target.checked,
                      }))
                    }
                    disabled={structuralLocked}
                    aria-disabled={structuralLocked}
                  />
                  Allow multiple selections
                </label>
                {draft.allowMultiple ? (
                  <div className="space-y-2">
                    <label
                      htmlFor={maxSelectionsId}
                      className="block text-sm font-medium text-card-foreground"
                    >
                      Max selections
                    </label>
                    <input
                      id={maxSelectionsId}
                      type="number"
                      min={2}
                      value={draft.maxSelections}
                      onChange={(event) =>
                        setDraft((previous) => ({
                          ...previous,
                          maxSelections: event.target.value,
                        }))
                      }
                      disabled={structuralLocked}
                      aria-disabled={structuralLocked}
                      aria-invalid={fieldIsInvalid("maxSelections")}
                      aria-describedby={fieldDescribedBy("maxSelections")}
                      className="w-40 rounded-xl border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      A whole number from 2 up to the number of options
                      {draft.allowWriteIn ? " plus write-in" : ""}. Turn off &quot;Allow multiple
                      selections&quot; for single-choice.
                    </p>
                  </div>
                ) : null}
                <label className="flex items-center gap-2 text-sm text-card-foreground">
                  <input
                    type="checkbox"
                    className="accent-brand-primary"
                    checked={draft.allowWriteIn}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        allowWriteIn: event.target.checked,
                      }))
                    }
                    disabled={structuralLocked}
                    aria-disabled={structuralLocked}
                  />
                  Allow write-in "Other"
                </label>
              </div>
            ) : null}

            <label className="flex items-center gap-2 text-sm text-card-foreground">
              <input
                type="checkbox"
                className="accent-brand-primary"
                checked={draft.required}
                onChange={(event) =>
                  setDraft((previous) => ({ ...previous, required: event.target.checked }))
                }
              />
              Required poll
            </label>

            <div className="rounded-xl border border-border bg-card p-3">
              <button
                type="button"
                onClick={() => setAdvancedExpanded((expanded) => !expanded)}
                aria-expanded={advancedExpanded}
                aria-controls={`${reactId}-advanced-settings`}
                className="flex min-h-[40px] w-full items-center justify-between gap-3 rounded-lg px-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span>
                  <span className="block text-sm font-semibold text-card-foreground">Advanced settings</span>
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    {draft.required
                      ? pollReminderSummary(draft.reminders, inheritedReminders, "embedded")
                      : "Notifications apply when this poll is required"}
                  </span>
                </span>
                {advancedExpanded
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
              </button>
              {advancedExpanded ? (
                <div id={`${reactId}-advanced-settings`} className="mt-3">
                  {draft.required ? (
                    <PollNotificationSettings
                      idPrefix={`${reactId}-embedded-poll`}
                      group={group}
                      value={draft.reminders}
                      inheritedValue={inheritedReminders}
                      onChange={(reminders) =>
                        setDraft((previous) => ({ ...previous, reminders }))
                      }
                      scope="embedded"
                      inheritedLabel="session notification defaults"
                    />
                  ) : (
                    <p className="rounded-lg bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                      Mark this poll as required to send automatic deadline reminders. Optional add-on polls do not pester participants.
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-card-foreground">Options</h3>
                <button
                  type="button"
                  onClick={addOption}
                  disabled={structuralLocked}
                  aria-disabled={structuralLocked}
                  className="inline-flex min-h-[32px] items-center rounded-full border border-border px-3 py-1 text-xs font-semibold text-card-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Add option
                </button>
              </div>
              <div className="space-y-2">
                {draft.options.map((option, index) => {
                  const optionInputId = `${reactId}-option-${option.id}`;
                  const optionLabel = option.label || `option ${index + 1}`;
                  return (
                    <div key={option.id} className="rounded-lg border border-border px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <label htmlFor={optionInputId} className="sr-only">
                          {`Option ${index + 1}`}
                        </label>
                        <input
                          id={optionInputId}
                          value={option.label}
                          onChange={(event) => updateOption(option.id, { label: event.target.value })}
                          placeholder={`Option ${index + 1}`}
                          disabled={structuralLocked}
                          aria-disabled={structuralLocked}
                          aria-invalid={index === 0 && fieldIsInvalid("options")}
                          aria-describedby={index === 0 ? fieldDescribedBy("options") : undefined}
                          className="min-w-[180px] flex-1 rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70"
                        />
                        <button
                          type="button"
                          onClick={() => moveOption(option.id, "up")}
                          disabled={index === 0 || structuralLocked}
                          aria-disabled={index === 0 || structuralLocked}
                          aria-label={`Move ${optionLabel} up`}
                          className="inline-flex min-h-[32px] items-center rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          onClick={() => moveOption(option.id, "down")}
                          disabled={index === draft.options.length - 1 || structuralLocked}
                          aria-disabled={index === draft.options.length - 1 || structuralLocked}
                          aria-label={`Move ${optionLabel} down`}
                          className="inline-flex min-h-[32px] items-center rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDraft((previous) => ({
                              ...previous,
                              noteEditor: { optionId: option.id, tab: "write", value: option.note || "" },
                            }))
                          }
                          aria-label={`${option.note ? "Edit" : "Add"} note for ${optionLabel}`}
                          className="inline-flex min-h-[32px] items-center rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {option.note ? "Edit note" : "Add note"}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeOption(option.id)}
                          disabled={draft.options.length <= 2 || structuralLocked}
                          aria-disabled={draft.options.length <= 2 || structuralLocked}
                          className="inline-flex min-h-[32px] items-center rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 transition-colors hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 dark:border-rose-600 dark:text-rose-300 dark:hover:bg-rose-950/30"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {hasDuplicateOptionLabel ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="text-[11px] text-muted-foreground"
                >
                  Two options share the same label — voters may find them hard to tell apart.
                </p>
              ) : null}
            </div>

            <p className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Vote privacy (visibility &amp; anonymity) is inherited from this
              session&apos;s settings, so every add-on poll stays consistent with the
              session vote. Change it in the session&apos;s vote-privacy settings.
            </p>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full"
            >
              {saving ? "Saving..." : initialPoll ? "Save poll" : "Add poll"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(draft.noteEditor.optionId)}
        onOpenChange={(nextOpen) => {
          if (nextOpen) return;
          setDraft((previous) => ({
            ...previous,
            noteEditor: { optionId: null, tab: "write", value: "" },
          }));
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Option note</DialogTitle>
            <DialogDescription>
              Add markdown details for {selectedOption?.label || "this option"}.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-3">
            <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs">
              <button
                type="button"
                onClick={() =>
                  setDraft((previous) => ({
                    ...previous,
                    noteEditor: { ...previous.noteEditor, tab: "write" },
                  }))
                }
                aria-pressed={draft.noteEditor.tab === "write"}
                className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  draft.noteEditor.tab === "write"
                    ? "bg-brand-primary text-white"
                    : "text-muted-foreground hover:text-card-foreground"
                }`}
              >
                Write
              </button>
              <button
                type="button"
                onClick={() =>
                  setDraft((previous) => ({
                    ...previous,
                    noteEditor: { ...previous.noteEditor, tab: "preview" },
                  }))
                }
                aria-pressed={draft.noteEditor.tab === "preview"}
                className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  draft.noteEditor.tab === "preview"
                    ? "bg-brand-primary text-white"
                    : "text-muted-foreground hover:text-card-foreground"
                }`}
              >
                Preview
              </button>
            </div>
            {draft.noteEditor.tab === "write" ? (
              <>
                <label htmlFor={noteTextareaId} className="sr-only">
                  Option note
                </label>
                <textarea
                  id={noteTextareaId}
                  value={draft.noteEditor.value}
                  onChange={(event) =>
                    setDraft((previous) => ({
                      ...previous,
                      noteEditor: { ...previous.noteEditor, value: event.target.value },
                    }))
                  }
                  rows={6}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </>
            ) : (
              <PollMarkdownContent
                content={draft.noteEditor.value}
                fallback="_No note_"
                className="rounded-lg border border-border bg-card px-3 py-2"
              />
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setDraft((previous) => ({
                  ...previous,
                  noteEditor: { optionId: null, tab: "write", value: "" },
                }))
              }
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!draft.noteEditor.optionId) return;
                updateOption(draft.noteEditor.optionId, { note: draft.noteEditor.value });
                setDraft((previous) => ({
                  ...previous,
                  noteEditor: { optionId: null, tab: "write", value: "" },
                }));
              }}
              className="rounded-full"
            >
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
